/*
 * 取扱店舗の一覧を Instagram の固定投稿から作る
 * sections/iroiro-store-page.liquid
 *
 * 取得先は social セクションと同じ中継役の Cloudflare Worker（別リポジトリ）。
 *
 *   { ok, posts: [...], profile, pinned: { permalink, image, caption, ... } }
 *
 * 【気をつける点】
 * ・Worker は失敗しても HTTP 200 で ok:false を返す。
 *   res.ok（ステータス）だけ見ていると失敗に気付けない
 * ・画像URLは署名付きで失効する。保存や再ホストはしない（表示のみ）
 * ・pinned.timestamp は最初に投稿した日で、更新日ではない。
 *   同じ投稿を編集して使い回しているため、日付はキャプションの先頭から取る
 */
(function () {
  'use strict';

  /* 「2026.6.1」「2026/6/1」のような、単独で置かれた日付の行 */
  var DATE_LINE = /^\d{4}\s*[./-]\s*\d{1,2}\s*[./-]\s*\d{1,2}$/;

  /* 「4/1~12/31」のような期間の行。波線は半角・全角どちらもありうる */
  var PERIOD_LINE = /^\d{1,2}\s*\/\s*\d{1,2}\s*[~〜\-–—]\s*\d{1,2}\s*\/\s*\d{1,2}$/;

  /* 「@handle」だけの行 */
  var HANDLE_LINE = /^@[A-Za-z0-9._]+$/;

  /*
   * キャプションを塊に分ける。
   *
   * 手で書かれた文章なので、決まった形に頼りすぎると
   * 書き方が少し変わっただけで何も出なくなる。
   * 空行で区切って塊にし、塊ごとに「何に見えるか」で振り分ける。
   * どれにも当てはまらない塊は、そのまま文章として出す（消さない）。
   */
  function parse(caption) {
    var lines = String(caption || '').split('\n');

    // 末尾のハッシュタグは落とす。店舗の情報ではないため
    while (lines.length && (lines[lines.length - 1].trim() === '' || lines[lines.length - 1].trim().charAt(0) === '#')) {
      lines.pop();
    }

    var blocks = [];
    var current = [];
    lines.forEach(function (raw) {
      var line = raw.trim();
      if (line === '') {
        if (current.length) blocks.push(current);
        current = [];
        return;
      }
      current.push(line);
    });
    if (current.length) blocks.push(current);

    var result = { updated: '', items: [] };

    blocks.forEach(function (block, index) {
      /*
       * 先頭の塊は日付とキャプション自身の見出し。
       * 日付は拾い、見出しが1行だけならそれは捨てる
       * （ページ側にも見出しがあり、二重になるため）。
       * 2行以上あるときは何か書かれているとみなして残す。
       */
      if (index === 0 && DATE_LINE.test(block[0])) {
        result.updated = block[0];
        block = block.slice(1);
        if (block.length <= 1) return;
      }

      var handle = '';
      var period = '';
      var names = [];

      block.forEach(function (line) {
        if (HANDLE_LINE.test(line)) {
          handle = line.slice(1);
        } else if (PERIOD_LINE.test(line)) {
          period = line;
        } else {
          names.push(line);
        }
      });

      if (handle) {
        /*
         * 区分の見出し（「委託店舗さま」など）は、次の店舗との間に
         * 空行が無いことがあり、同じ塊に入ってくる。
         * 店舗名は @handle の直前の行なので、それより前に残った行は
         * 見出しとして先に出す。
         */
        while (names.length > 1) {
          result.items.push({ type: 'heading', text: names.shift() });
        }
        result.items.push({ type: 'shop', name: names[0] || '', period: period, handle: handle });
      } else if (block.length === 1 && block[0].length <= 20) {
        // 1行だけの短い塊は区分の見出し（「委託店舗さま」など）とみなす
        result.items.push({ type: 'heading', text: block[0] });
      } else {
        result.items.push({ type: 'note', text: block.join('\n') });
      }
    });

    return result;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function init(root) {
    (root || document).querySelectorAll('[data-iroiro-store-feed]').forEach(function (box) {
      if (box.dataset.iroiroStoreReady === 'true') return;
      box.dataset.iroiroStoreReady = 'true';

      var section = box.closest('.iroiro-store-page');
      var fallback = section && section.querySelector('[data-iroiro-store-fallback]');
      var feed = box.dataset.feed;

      function giveUp() {
        box.hidden = true;
        if (fallback) fallback.hidden = false;
      }

      if (!feed) {
        giveUp();
        return;
      }

      function render(pinned) {
        var data = parse(pinned.caption);
        box.textContent = '';

        if (data.updated) {
          var label = box.dataset.updatedLabel || '';
          box.appendChild(el('p', 'iroiro-store-page__updated', (label + ' ' + data.updated).trim()));
        }

        /*
         * 店舗は ul にまとめる。区分の見出しが出てきたらそこで一度閉じ、
         * 見出しを挟んでから次の ul を開く。
         * 見出しを ul の中に入れると、読み上げで一覧の項目として扱われる。
         */
        var list = null;
        data.items.forEach(function (item) {
          if (item.type === 'shop') {
            if (!list) {
              list = el('ul', 'iroiro-store-page__list');
              box.appendChild(list);
            }

            var li = el('li', 'iroiro-store-page__shop');

            /*
             * 期間は店舗名の上。日付だけだと何の日付か分からないので、
             * 設定の言葉（既定は「期間限定」）を頭に付ける。
             * 空にすれば日付だけになる。
             */
            if (item.period) {
              var pill = el('span', 'iroiro-store-page__period');
              var periodLabel = box.dataset.periodLabel;
              if (periodLabel) pill.appendChild(el('span', 'iroiro-store-page__period-label', periodLabel));
              pill.appendChild(el('span', 'iroiro-store-page__period-date', item.period));
              li.appendChild(pill);
            }

            /*
             * 店舗名とアカウントは同じ行。期間はこの行の外に出したいので、
             * ここだけ別の箱に入れている。
             */
            var row = el('div', 'iroiro-store-page__row');
            if (item.name) row.appendChild(el('span', 'iroiro-store-page__name', item.name));

            var link = el('a', 'iroiro-store-page__handle', '@' + item.handle);
            link.href = 'https://www.instagram.com/' + item.handle + '/';
            link.target = '_blank';
            link.rel = 'noopener';
            row.appendChild(link);

            li.appendChild(row);
            list.appendChild(li);
            return;
          }

          list = null;

          if (item.type === 'heading') {
            box.appendChild(el('h3', 'iroiro-store-page__group', item.text));
          } else {
            box.appendChild(el('p', 'iroiro-store-page__note', item.text));
          }
        });

        /*
         * 元の投稿へ返す（Instagram の利用条件で求められている）。
         * 画像も出すと、Instagram で見ているものと同じだと分かりやすい。
         */
        if (pinned.permalink) {
          var foot = el('p', 'iroiro-store-page__source');
          var source = el('a', '', box.dataset.postLabel || 'Instagram');
          source.href = pinned.permalink;
          source.target = '_blank';
          source.rel = 'noopener';
          foot.appendChild(source);
          box.appendChild(foot);
        }
      }

      fetch(feed, { headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          // 失敗しても HTTP 200 で返るので、ok と中身の両方を見る
          if (!data || data.ok !== true || !data.pinned || !data.pinned.caption) {
            giveUp();
            return;
          }
          render(data.pinned);
        })
        .catch(giveUp);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
